import React, { useRef, useState, useLayoutEffect } from 'react';
import { Measure as MeasureType, ActiveChord, TimeSignature, AppMode } from '../types';
import Measure from './Measure';
import { VoltaPosition } from './MeasureAnnotations';

const getVoltaPosition = (chart: MeasureType[], index: number): VoltaPosition | undefined => {
  const ending = chart[index]?.annotations?.ending;
  if (!ending) return undefined;
  const prevSame = index > 0 && chart[index - 1]?.annotations?.ending === ending;
  const nextSame = index < chart.length - 1 && chart[index + 1]?.annotations?.ending === ending;
  if (!prevSame && !nextSame) return 'only';
  if (!prevSame && nextSame)  return 'first';
  if (prevSame  && nextSame)  return 'middle';
  return 'last';
};

interface ChartGridProps {
  mode: AppMode;
  chart: MeasureType[];
  activeChord: ActiveChord | null;
  currentMeasure: number | null;
  currentChordIndex: number | null;
  startMeasureIndex: number;
  invalidChords: Record<string, string | null>;
  clipboard: (string | null)[] | null;
  onSetStartMeasure: (index: number) => void;
  onChordClick: (measureIndex: number, chordIndex: number) => void;
  onSubdivisionChange: (measureIndex: number, newSubdivision: number) => void;
  onPlayChord: (chord: string) => void;
  onCopyMeasure: (measureIndex: number) => void;
  onPasteMeasure: (measureIndex: number) => void;
  onChordHover: (chord: string, element: HTMLElement) => void;
  onChordLeave: () => void;
  onLyricChange: (measureIndex: number, newLyric: string) => void;
  onInsertMeasures: (afterMeasureIndex: number) => void;
  onDeleteMeasure: (measureIndex: number) => void;
  timeSignature: TimeSignature;
  lyricsFontSize?: number;
  customGridClasses?: string; // For CSS-based responsive modes
  customGridStyle?: React.CSSProperties; // For measurement-based height mode
  inspectorMode?: 'piano' | 'guitar';
  inspectorEnabled?: boolean;
  chartHasFine?: boolean;
  chartHasToCoda?: boolean;
}

const ChartGrid: React.FC<ChartGridProps> = ({
  mode,
  chart,
  activeChord,
  currentMeasure,
  currentChordIndex,
  startMeasureIndex,
  invalidChords,
  clipboard,
  onSetStartMeasure,
  onChordClick,
  onSubdivisionChange,
  onPlayChord,
  onCopyMeasure,
  onPasteMeasure,
  onChordHover,
  onChordLeave,
  onLyricChange,
  onInsertMeasures,
  onDeleteMeasure,
  timeSignature,
  lyricsFontSize,
  customGridClasses,
  customGridStyle,
  inspectorMode = 'piano',
  inspectorEnabled = false,
  chartHasFine = false,
  chartHasToCoda = false
}) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const [gigFontSize, setGigFontSize] = useState(24);

  useLayoutEffect(() => {
    const gridElement = gridRef.current;
    if ((mode !== 'gig' && mode !== 'sandbox') || !gridElement) return;

    const calculateFontSize = () => {
        const containerWidth = gridElement.clientWidth;
        // Align with Tailwind's 'md' breakpoint (768px)
        const numColumns = containerWidth < 768 ? 2 : 4; 
        const gap = 4; // Corresponds to Tailwind's gap-1
        const cellWidth = (containerWidth - (numColumns - 1) * gap) / numColumns;
        
        // Base font size on a fraction of the cell width for scalability
        let newSize = cellWidth / 4.5;

        // Clamp the font size to a readable range
        const MIN_FONT_SIZE = 12;
        const MAX_FONT_SIZE = 48;
        newSize = Math.max(MIN_FONT_SIZE, Math.min(newSize, MAX_FONT_SIZE));
        
        setGigFontSize(newSize);
    };

    // Use ResizeObserver to recalculate on container resize or orientation change.
    const resizeObserver = new ResizeObserver(calculateFontSize);
    resizeObserver.observe(gridElement);

    // Initial calculation.
    calculateFontSize();

    // Cleanup observer on unmount.
    return () => resizeObserver.disconnect();
  }, [mode]);

  const containerClasses = customGridClasses || (mode === 'gig'
    ? 'grid grid-cols-2 md:grid-cols-4 gap-1'
    : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-96');

  // For measurement-based height mode, combine base classes with inline styles
  const gridStyle = customGridStyle ? {
    ...customGridStyle,
    display: 'grid', 
    gap: mode === 'gig' ? '0.25rem' : '1rem',
    paddingBottom: mode === 'gig' ? '0' : '24rem'
  } : undefined;

  return (
    <div 
      className={gridStyle ? 'w-full max-w-7xl mx-auto' : containerClasses} 
      style={gridStyle}
      ref={gridRef} 
      data-debug-id="Level-1:ChartGrid"
    >
      {chart.map((measure, index) => (
        <Measure
          key={measure.id}
          mode={mode}
          measure={measure}
          measureIndex={index}
          activeChord={activeChord}
          isPlaying={currentMeasure === index}
          playingChordIndex={currentMeasure === index ? currentChordIndex : null}
          isStartMeasure={startMeasureIndex === index}
          onSetStartMeasure={onSetStartMeasure}
          onChordClick={onChordClick}
          onSubdivisionChange={onSubdivisionChange}
          onPlayChord={onPlayChord}
          timeSignature={timeSignature}
          invalidChords={invalidChords}
          clipboard={clipboard}
          onCopyMeasure={onCopyMeasure}
          onPasteMeasure={onPasteMeasure}
          onChordHover={onChordHover}
          onChordLeave={onChordLeave}
          onLyricChange={onLyricChange}
          onInsertMeasures={onInsertMeasures}
          onDeleteMeasure={onDeleteMeasure}
          gigFontSize={gigFontSize}
          lyricsFontSize={lyricsFontSize}
          inspectorMode={inspectorMode}
          inspectorEnabled={inspectorEnabled}
          chartHasFine={chartHasFine}
          chartHasToCoda={chartHasToCoda}
          voltaPosition={getVoltaPosition(chart, index)}
          previousMeasureHasEndRepeat={index > 0 ? !!chart[index - 1]?.annotations?.endRepeat : false}
        />
      ))}
    </div>
  );
};

export default ChartGrid;